import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { ExternalLink, MapPin, Sparkles, ShieldCheck, Search as SearchIcon, Info } from "lucide-react";
import { Link } from "react-router-dom";

// Marketplace URL builders — every one opens in a new tab; nothing is scraped.
// Prefilling is best-effort per site; if a site changes params, users still land on the correct
// results page and can refine from there.

const MAKES = [
  "Acura", "Alfa Romeo", "Aston Martin", "Audi", "Bentley", "BMW", "Buick", "Cadillac", "Chevrolet",
  "Chrysler", "Dodge", "Ferrari", "Fiat", "Ford", "Genesis", "GMC", "Honda", "Hyundai", "Infiniti",
  "Jaguar", "Jeep", "Kia", "Lamborghini", "Land Rover", "Lexus", "Lincoln", "Lucid", "Maserati",
  "Mazda", "McLaren", "Mercedes-Benz", "Mini", "Mitsubishi", "Nissan", "Polestar", "Porsche", "Ram",
  "Rivian", "Rolls-Royce", "Subaru", "Tesla", "Toyota", "Volkswagen", "Volvo",
];

const CONDITIONS = [
  { v: "all", l: "New + Used" },
  { v: "new", l: "New" },
  { v: "used", l: "Used" },
  { v: "cpo", l: "Certified (CPO)" },
];

const YEARS = Array.from({ length: 15 }, (_, i) => 2026 - i);

function slugify(s) {
  return String(s || "").toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}

function buildLinks({ make, model, zip, radius, maxPrice, minYear, maxYear, condition }) {
  const makeSlug = slugify(make);
  const modelSlug = slugify(model);
  const modelCombined = make && model ? `${makeSlug}-${modelSlug}` : "";
  const q = encodeURIComponent([make, model].filter(Boolean).join(" "));

  // ---- Cars.com ----
  const carsParams = new URLSearchParams();
  if (makeSlug) carsParams.set("makes[]", makeSlug);
  if (modelSlug) carsParams.set("models[]", modelCombined);
  if (zip) { carsParams.set("zip", zip); carsParams.set("maximum_distance", String(radius)); }
  if (maxPrice) carsParams.set("list_price_max", String(maxPrice));
  if (minYear) carsParams.set("year_min", String(minYear));
  if (maxYear) carsParams.set("year_max", String(maxYear));
  const carsStock = condition === "new" ? "new" : condition === "cpo" ? "cpo" : condition === "used" ? "used" : "";
  if (carsStock) carsParams.set("stock_type", carsStock);
  const cars = `https://www.cars.com/shopping/results/?${carsParams.toString()}`;

  // ---- Autotrader ----
  // Path-based: /cars-for-sale/{new|used|all-cars}/{make}/{model}/{zip}?...
  const atPath = condition === "new" ? "new-cars" : condition === "used" ? "used-cars" : condition === "cpo" ? "certified-cars" : "all-cars";
  const atSegs = [atPath, makeSlug, modelSlug].filter(Boolean).join("/");
  const atParams = new URLSearchParams();
  if (zip) { atParams.set("searchRadius", String(radius)); }
  if (maxPrice) atParams.set("maxPrice", String(maxPrice));
  if (minYear) atParams.set("startYear", String(minYear));
  if (maxYear) atParams.set("endYear", String(maxYear));
  const atSuffix = zip ? `/${zip}` : "";
  const autotrader = `https://www.autotrader.com/cars-for-sale/${atSegs}${atSuffix}?${atParams.toString()}`;

  // ---- CarGurus ----
  const cgParams = new URLSearchParams();
  cgParams.set("sourceContext", "carGurusHomePageModel");
  if (zip) { cgParams.set("zip", zip); cgParams.set("distance", String(radius)); }
  if (makeSlug) cgParams.set("newSearchFromOverviewPage", "true");
  if (maxPrice) cgParams.set("maxPrice", String(maxPrice));
  if (minYear) cgParams.set("startYear", String(minYear));
  if (maxYear) cgParams.set("endYear", String(maxYear));
  cgParams.set("entitySelectingHelper.selectedEntity", modelCombined ? modelCombined : (makeSlug || ""));
  const cargurus = `https://www.cargurus.com/Cars/inventorylisting/viewDetailsFilterViewInventoryListing.action?${cgParams.toString()}`;

  // ---- TrueCar ----
  const tcSegs = ["used-cars-for-sale", "listings", makeSlug, modelSlug].filter(Boolean).join("/");
  const tcSuffix = zip ? `/location-${zip}/` : "/";
  const tcParams = new URLSearchParams();
  if (radius) tcParams.set("searchRadius", String(radius));
  if (maxPrice) tcParams.set("maxPrice", String(maxPrice));
  if (minYear) tcParams.set("yearLow", String(minYear));
  if (maxYear) tcParams.set("yearHigh", String(maxYear));
  const truecar = `https://www.truecar.com/${tcSegs}${tcSuffix}?${tcParams.toString()}`;

  // ---- AutoTempest (meta-search) ----
  const teParams = new URLSearchParams();
  if (makeSlug) teParams.set("make", makeSlug);
  if (modelSlug) teParams.set("model", modelSlug);
  if (zip) { teParams.set("zip", zip); teParams.set("radius", String(radius)); }
  if (maxPrice) teParams.set("maxprice", String(maxPrice));
  if (minYear) teParams.set("minyear", String(minYear));
  if (maxYear) teParams.set("maxyear", String(maxYear));
  const autotempest = `https://www.autotempest.com/results?${teParams.toString()}`;

  // ---- Facebook Marketplace (best-effort — no zip param supported publicly) ----
  const fbParams = new URLSearchParams();
  if (q) fbParams.set("query", decodeURIComponent(q));
  if (maxPrice) fbParams.set("maxPrice", String(maxPrice));
  if (minYear) fbParams.set("minYear", String(minYear));
  if (maxYear) fbParams.set("maxYear", String(maxYear));
  const facebook = `https://www.facebook.com/marketplace/category/vehicles?${fbParams.toString()}`;

  return { cars, autotrader, cargurus, truecar, autotempest, facebook };
}

const SITES = [
  { key: "cars", name: "Cars.com", desc: "Largest US inventory · dealer + private", accent: "from-red-500/20 to-red-500/5", ring: "ring-red-500/20", bestFor: "Deepest inventory" },
  { key: "autotrader", name: "Autotrader", desc: "Franchise dealers · new + used", accent: "from-orange-500/20 to-orange-500/5", ring: "ring-orange-500/20", bestFor: "Franchise deals" },
  { key: "cargurus", name: "CarGurus", desc: "Deal Score ranking · price transparency", accent: "from-blue-500/20 to-blue-500/5", ring: "ring-blue-500/20", bestFor: "Price analytics" },
  { key: "truecar", name: "TrueCar", desc: "Certificate pricing · nationwide", accent: "from-emerald-500/20 to-emerald-500/5", ring: "ring-emerald-500/20", bestFor: "Upfront pricing" },
  { key: "autotempest", name: "AutoTempest", desc: "Meta-search across all sites", accent: "from-purple-500/20 to-purple-500/5", ring: "ring-purple-500/20", bestFor: "One-stop scan" },
  { key: "facebook", name: "Facebook Marketplace", desc: "Private sellers · local · best-effort filters", accent: "from-sky-500/20 to-sky-500/5", ring: "ring-sky-500/20", bestFor: "Private sellers" },
];

// Anonymous per-browser-session id. Persists ONLY for the tab session — cleared on close.
function getAnonymousSessionId() {
  try {
    let sid = sessionStorage.getItem("ms_session_id");
    if (!sid) {
      sid = `ms_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
      sessionStorage.setItem("ms_session_id", sid);
    }
    return sid;
  } catch {
    return `ms_${Math.random().toString(36).slice(2, 12)}`;
  }
}

function trackClick(marketplace) {
  // Fire-and-forget. Never block the user or throw into console.
  try {
    api.post("/marketplace-click", { marketplace, session_id: getAnonymousSessionId() }).catch(() => {});
  } catch { /* ignore */ }
}

export default function MarketplaceSearch() {
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [zip, setZip] = useState("");
  const [radius, setRadius] = useState(50);
  const [maxPrice, setMaxPrice] = useState(60000);
  const [minYear, setMinYear] = useState(2020);
  const [maxYear, setMaxYear] = useState(2026);
  const [condition, setCondition] = useState("all");

  const links = useMemo(
    () => buildLinks({ make, model, zip, radius, maxPrice, minYear, maxYear, condition }),
    [make, model, zip, radius, maxPrice, minYear, maxYear, condition],
  );

  const openAll = () => {
    SITES.forEach((s, i) => {
      trackClick(s.key);
      setTimeout(() => window.open(links[s.key], "_blank", "noopener,noreferrer"), i * 200);
    });
  };

  const clearAll = () => {
    setMake(""); setModel(""); setZip(""); setRadius(50); setMaxPrice(60000); setMinYear(2020); setMaxYear(2026); setCondition("all");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="marketplace-search-page">
      <div className="mb-8">
        <p className="label-mono mb-3">External Marketplace Search</p>
        <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter">Search every marketplace at once.</h1>
        <p className="text-gray-400 max-w-3xl mt-4">
          Fill in what you&apos;re looking for, then jump straight into results on Cars.com, Autotrader, CarGurus, TrueCar, AutoTempest and Facebook Marketplace — all prefilled with your filters. We don&apos;t scrape or middleman these sites; you shop directly.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mb-10">
        {/* Filter form */}
        <div className="lg:col-span-5 bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6 h-fit sticky top-24">
          <div className="flex items-center gap-2 mb-5">
            <SearchIcon className="w-4 h-4 text-silver" />
            <div className="label-mono">Your search</div>
          </div>

          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Make</div>
                <select
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  className="w-full h-11 rounded-lg bg-[#050507] border border-[#1A1A24] text-white px-3 text-sm"
                  data-testid="ms-make-select"
                >
                  <option value="">Any make</option>
                  {MAKES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Model</div>
                <Input
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="e.g. X5, Cayenne, Model Y"
                  className="bg-[#050507] border-[#1A1A24] text-white h-11 text-sm"
                  data-testid="ms-model-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> ZIP
                </div>
                <Input
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="10001"
                  className="bg-[#050507] border-[#1A1A24] text-white h-11 text-sm font-mono"
                  data-testid="ms-zip-input"
                />
              </div>
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Condition</div>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="w-full h-11 rounded-lg bg-[#050507] border border-[#1A1A24] text-white px-3 text-sm"
                  data-testid="ms-condition-select"
                >
                  {CONDITIONS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] text-gray-500 uppercase tracking-wider">Search Radius</div>
                <div className="text-xs text-gray-400 font-mono">{radius} mi</div>
              </div>
              <Slider
                min={10} max={500} step={10}
                value={[radius]}
                onValueChange={(v) => setRadius(v[0])}
                data-testid="ms-radius-slider"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] text-gray-500 uppercase tracking-wider">Max Price</div>
                <div className="text-xs text-gray-400 font-mono">${maxPrice.toLocaleString()}</div>
              </div>
              <Slider
                min={5000} max={250000} step={2500}
                value={[maxPrice]}
                onValueChange={(v) => setMaxPrice(v[0])}
                data-testid="ms-price-slider"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">From Year</div>
                <select
                  value={minYear}
                  onChange={(e) => setMinYear(Number(e.target.value))}
                  className="w-full h-11 rounded-lg bg-[#050507] border border-[#1A1A24] text-white px-3 text-sm"
                  data-testid="ms-min-year-select"
                >
                  {YEARS.slice().reverse().map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">To Year</div>
                <select
                  value={maxYear}
                  onChange={(e) => setMaxYear(Number(e.target.value))}
                  className="w-full h-11 rounded-lg bg-[#050507] border border-[#1A1A24] text-white px-3 text-sm"
                  data-testid="ms-max-year-select"
                >
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={openAll}
                className="flex-1 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm inline-flex items-center justify-center gap-2"
                data-testid="ms-open-all-btn"
              >
                <Sparkles className="w-4 h-4" /> Search All 6 Sites
              </button>
              <button
                onClick={clearAll}
                className="px-4 py-2.5 rounded-full border border-white/15 text-xs hover:bg-white/5"
                data-testid="ms-clear-btn"
              >
                Clear
              </button>
            </div>

            <div className="text-[11px] text-gray-500 flex items-start gap-2 pt-2 border-t border-white/5">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>Each button opens the site directly. We never proxy or scrape these marketplaces — your search runs on their servers with their terms.</span>
            </div>
          </div>
        </div>

        {/* Marketplace launchers */}
        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-2 gap-4">
            {SITES.map((s) => (
              <a
                key={s.key}
                href={links[s.key]}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick(s.key)}
                className={`group relative overflow-hidden bg-gradient-to-br ${s.accent} border border-white/10 rounded-2xl p-5 hover:border-white/25 hover:ring-1 ${s.ring} transition-all`}
                data-testid={`ms-launch-${s.key}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-display text-xl mb-1">{s.name}</div>
                    <div className="text-xs text-gray-400 leading-snug">{s.desc}</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" />
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500">Best for</span>
                  <span className="text-xs font-medium">{s.bestFor}</span>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-6 p-5 rounded-2xl bg-emerald-500/5 border border-emerald-500/20">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium mb-1 text-emerald-200">Prefer white-glove?</div>
                <div className="text-emerald-100/80 text-xs leading-relaxed">
                  Skip the tabs. Tell our broker team exactly what you want and we&apos;ll source it from our authorized dealer network — no dealer visits, no back-and-forth.
                </div>
                <div className="flex gap-2 mt-3">
                  <Link to="/find-my-car" className="text-xs px-4 py-2 rounded-full silver-gradient text-[#050507] font-bold" data-testid="ms-find-my-car-link">
                    Request This Vehicle
                  </Link>
                  <Link to="/inventory" className="text-xs px-4 py-2 rounded-full border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10">
                    Browse Authorized Inventory
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-gray-500 max-w-3xl">
        Trademarks belong to their respective owners. Links open in new tabs and are provided for user convenience only. 555 Auto Brokers is not affiliated with these marketplaces and does not aggregate or store their inventory.
      </p>
    </div>
  );
}
